import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FiCheck, FiCopy, FiHome, FiSearch, FiX } from 'react-icons/fi';
import { deleteProperty } from '../../store/thunks/propertyActions';

const normalize = (value = '') => String(value).toLowerCase().trim();

const formatAddress = (address) =>
  [address?.line1, address?.line2, address?.city, address?.state, address?.zipCode].filter(Boolean).join(' ');

const copyToClipboard = async (text) => {
  if (!text) return false;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_error) {
    // fall through to the legacy path
  }
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.opacity = '0';
  document.body.appendChild(textArea);
  textArea.select();
  let copied = false;
  try {
    copied = document.execCommand('copy');
  } catch (_error) {
    copied = false;
  }
  document.body.removeChild(textArea);
  return copied;
};

const HostPropertiesTable = ({ data = [], loading = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  const properties = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const filtered = useMemo(() => {
    const q = normalize(query);
    if (!q) return properties;
    const terms = q.split(/\s+/).filter(Boolean);
    return properties.filter((entry) => {
      const haystack = normalize(
        [formatAddress(entry.address), entry.listingTitle, entry.description, entry.uniqueId].filter(Boolean).join(' ')
      );
      return terms.every((term) => haystack.includes(term));
    });
  }, [properties, query]);

  const handleDelete = (entry) => {
    if (window.confirm(`Delete ${entry.address?.line1 || 'this property'}? This cannot be undone.`)) {
      dispatch(deleteProperty(entry._id));
    }
  };

  const handleCopy = async (entry) => {
    const copied = await copyToClipboard(entry.uniqueLink);
    if (!copied) return;
    setCopiedId(entry._id);
    setTimeout(() => setCopiedId((current) => (current === entry._id ? null : current)), 2000);
  };

  return (
    <section className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center gap-3 border-b border-zinc-100 px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900">Properties</h3>
          <p className="text-xs text-zinc-500">
            {filtered.length === properties.length
              ? `${properties.length} ${properties.length === 1 ? 'property' : 'properties'}`
              : `${filtered.length} of ${properties.length} shown`}
          </p>
        </div>
        <div className="relative ml-auto w-full laptop:w-72">
          <FiSearch className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Filter by address, title, or ID"
            aria-label="Filter properties"
            className="h-8 w-full rounded-lg border border-zinc-200 bg-zinc-50 pl-8 pr-7 text-xs text-zinc-900 outline-none transition focus:border-indigo-300 focus:bg-white focus:ring-2 focus:ring-indigo-100"
          />
          {query ? (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Clear property filter"
              className="absolute right-1.5 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
            >
              <FiX className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
      </div>

      {loading ? (
        <div className="space-y-2 p-4">
          {[0, 1, 2].map((row) => (
            <div key={row} className="h-12 animate-pulse rounded-xl bg-zinc-100" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-zinc-100 text-zinc-400">
            <FiHome className="h-5 w-5" />
          </div>
          <p className="text-sm font-semibold text-zinc-900">
            {properties.length ? 'No properties match this filter' : 'No properties yet'}
          </p>
          {properties.length ? (
            <button type="button" onClick={() => setQuery('')} className="text-xs font-medium text-indigo-600 hover:text-indigo-800">
              Clear filter
            </button>
          ) : null}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-zinc-50 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-4 py-2.5">Address</th>
                <th className="px-4 py-2.5">Listing</th>
                <th className="px-4 py-2.5">Guest link</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {filtered.map((entry) => {
                const copied = copiedId === entry._id;
                const detailPath = `/hostList/${entry.hostID}/${entry._id}`;
                return (
                  <tr
                    key={entry._id}
                    onClick={() => navigate(detailPath)}
                    className="cursor-pointer transition hover:bg-zinc-50"
                  >
                    <td className="max-w-[320px] px-4 py-3">
                      <p className="truncate font-medium text-zinc-900">
                        {[entry.address?.line1, entry.address?.line2].filter(Boolean).join(' ') || '—'}
                      </p>
                      <p className="truncate text-xs text-zinc-500">
                        {[entry.address?.city, entry.address?.state, entry.address?.zipCode].filter(Boolean).join(', ') || '—'}
                      </p>
                    </td>
                    <td className="max-w-[260px] px-4 py-3">
                      <p className="truncate text-zinc-700">{entry.listingTitle || entry.description || '—'}</p>
                      {entry.uniqueId ? <p className="truncate font-mono text-[11px] text-zinc-400">{entry.uniqueId}</p> : null}
                    </td>
                    <td className="px-4 py-3" onClick={(event) => event.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => handleCopy(entry)}
                        disabled={!entry.uniqueLink}
                        className={`inline-flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-40 ${
                          copied
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                            : 'border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50'
                        }`}
                      >
                        {copied ? <FiCheck className="h-3.5 w-3.5" /> : <FiCopy className="h-3.5 w-3.5" />}
                        {copied ? 'Copied' : 'Copy link'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right" onClick={(event) => event.stopPropagation()}>
                      <div className="inline-flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => navigate(detailPath)}
                          className="h-8 rounded-lg border border-zinc-200 bg-white px-2.5 text-xs font-medium text-zinc-700 transition hover:bg-zinc-50"
                        >
                          View
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate(`${detailPath}?edit=true`)}
                          className="h-8 rounded-lg border border-zinc-200 bg-white px-2.5 text-xs font-medium text-zinc-700 transition hover:bg-zinc-50"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(entry)}
                          className="h-8 rounded-lg border border-red-200 bg-white px-2.5 text-xs font-medium text-red-600 transition hover:bg-red-50"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default HostPropertiesTable;
